// Request schemas of the queue and correction endpoints (spec §1.12). Shared with the future client, so every
// message that is not a plain type or length complaint is German here and does not depend on the global error map.
import { z } from 'zod'

const id = z.string().trim().min(1).max(64)
const idOrNull = z.union([id, z.null()])
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'Bitte ein Datum im Format JJJJ-MM-TT angeben.' })
const cents = z.number().int({ message: 'Beträge werden in ganzen Cent angegeben.' })
const reason = z.string().trim().min(3, { message: 'Bitte einen Grund angeben (mindestens 3 Zeichen).' }).max(500)
const note = z.string().trim().min(1).max(500)

export const inboxQuerySchema = z.object({
  kind: z.string().trim().min(1).max(64).optional(),
  limit: z.coerce.number().int().min(1).max(5000).optional(),
})
export type InboxQuery = z.infer<typeof inboxQuerySchema>

export const dismissSchema = z.object({ note })
export type Dismiss = z.infer<typeof dismissSchema>

/**
 * PATCH /api/t/:id/zahlungen/:cinId — one flat body with exactly one of the five corrections, so the failure
 * message can name the missing half instead of the generic union complaint.
 */
export const zahlungPatchSchema = z
  .object({
    manual_deal_id: idOrNull.optional(),
    manual_product_id: idOrNull.optional(),
    ignored_reason: z.union([note, z.null()]).optional(),
    delta_id: id.optional(),
    paid_at: isoDate.optional(),
    net_cents: cents.optional(),
    gross_cents: cents.optional(),
    reason,
  })
  .superRefine((v, ctx) => {
    const chosen = [
      v.manual_deal_id !== undefined,
      v.manual_product_id !== undefined,
      v.ignored_reason !== undefined,
      v.paid_at !== undefined,
      v.net_cents !== undefined || v.gross_cents !== undefined,
    ].filter(Boolean).length
    if (chosen !== 1) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['reason'], message: 'Bitte genau eine Korrektur pro Aufruf angeben.' })
      return
    }
    if ((v.paid_at !== undefined || v.net_cents !== undefined || v.gross_cents !== undefined) && v.delta_id === undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['delta_id'], message: 'Für diese Korrektur fehlt die Zahlungs-Ereignis-ID.' })
    }
    if ((v.net_cents === undefined) !== (v.gross_cents === undefined)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['gross_cents'], message: 'Für die Netto/Brutto-Korrektur werden beide Beträge gebraucht.' })
    }
  })
export type ZahlungPatch = z.infer<typeof zahlungPatchSchema>

export const LEAD_SOURCES = ['intern', 'extern'] as const
const leadSourceOrNull = z.union([z.enum(LEAD_SOURCES), z.null()])

export const createDealSchema = z.object({
  product_id: id,
  closer_tc_id: idOrNull.optional(),
  setter_tc_id: idOrNull.optional(),
  host_tc_id: idOrNull.optional(),
  sonstige_tc_id: idOrNull.optional(),
  lead_source: leadSourceOrNull.optional(),
  signed_at: z.union([isoDate, z.null()]).optional(),
  reason,
})
export type CreateDeal = z.infer<typeof createDealSchema>

/** POST /api/t/:id/abschluesse — a deal with no HighLevel delivery and no payment behind it. */
export const createDealDirectSchema = createDealSchema.extend({
  customer_name: z.string().trim().min(1, 'Kundenname fehlt.').max(200),
  hl_contact_id: idOrNull,
  contract_value_cents: z.number().int().min(0).max(100_000_000),
})
export type CreateDealDirect = z.infer<typeof createDealDirectSchema>

export const refundDecisionSchema = z.object({ reason })
export type RefundDecision = z.infer<typeof refundDecisionSchema>

export const DEAL_STATUSES = ['aktiv', 'weiterlauf', 'beendet', 'storno', 'ignorieren', 'pruefen'] as const

export const abschlussPatchSchema = z
  .object({
    version: z.number().int().min(1),
    closer_tc_id: idOrNull.optional(),
    setter_tc_id: idOrNull.optional(),
    host_tc_id: idOrNull.optional(),
    sonstige_tc_id: idOrNull.optional(),
    host_rate_bp: z.number().int().min(0).max(10000).optional(),
    overhead_individual_cents: cents.optional(),
    lead_source: leadSourceOrNull.optional(),
    product_id: idOrNull.optional(),
    status: z.enum(DEAL_STATUSES).optional(),
    notes: z.union([z.string().max(2000), z.null()]).optional(),
    reason,
  })
  .superRefine((v, ctx) => {
    const touched = [
      'closer_tc_id', 'setter_tc_id', 'host_tc_id', 'sonstige_tc_id', 'host_rate_bp', 'overhead_individual_cents', 'lead_source', 'product_id', 'status', 'notes',
    ].some((k) => Object.prototype.hasOwnProperty.call(v, k))
    if (!touched) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['reason'], message: 'Bitte mindestens ein Feld ändern.' })
    }
  })
export type AbschlussPatch = z.infer<typeof abschlussPatchSchema>

/** POST /api/t/:id/abschluesse/:dealId/zusammenfuehren — :dealId bleibt, `streichen` wird ignoriert. */
export const zusammenfuehrenSchema = z.object({
  streichen: id,
  version: z.number().int().min(1),
  reason,
})
export type Zusammenfuehren = z.infer<typeof zusammenfuehrenSchema>

export const adjustmentSchema = z.object({
  recipient_tc_id: id,
  amount_cents: cents.refine((v) => v !== 0, { message: 'Der Betrag darf nicht 0 sein.' }),
  note,
  deal_id: idOrNull.optional(),
  role_code: z.string().regex(/^[A-Z][A-Z0-9_]{1,31}$/, { message: 'Rollen-Kürzel: Großbuchstaben, Ziffern und _.' }).optional(),
  role_label: z.string().trim().min(1).max(80).optional(),
  leistung_text: z.string().trim().min(1).max(200).optional(),
  reason,
})
export type Adjustment = z.infer<typeof adjustmentSchema>

export const writeOffSchema = z.object({
  settlement: z.enum(['extern_verrechnet', 'verzichtet']),
  reason,
})
export type WriteOff = z.infer<typeof writeOffSchema>

export const manualPaymentSchema = z.object({
  invoice_number: z.string().trim().min(1).max(64),
  hl_contact_id: idOrNull,
  customer_name: z.string().trim().min(1).max(200),
  invoice_name: z.string().trim().max(500),
  paid_at: z.union([isoDate, z.null()]),
  cumulative_paid_cents: z.number().int().min(1, { message: 'Der bezahlte Betrag muss größer als 0 sein.' }),
  invoice_net_cents: z.union([cents, z.null()]).optional(),
  invoice_gross_cents: z.union([cents, z.null()]).optional(),
  source_webhook_event_id: idOrNull.optional(),
  reason,
})
export type ManualPayment = z.infer<typeof manualPaymentSchema>

/** POST /api/t/:id/provisionen/bestaetigen — bis zu einem Stichtag oder genau diese Zeilen. */
export const bestaetigenSchema = z.object({
  bis: isoDate.optional(),
  ids: z.array(z.string().trim().min(1)).max(5000).optional(),
}).refine((v) => v.bis !== undefined || v.ids !== undefined, { message: 'Bitte einen Stichtag oder Zeilen angeben.', path: ['bis'] })
export type BestaetigenBody = z.infer<typeof bestaetigenSchema>
